import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { BUCKET_PALETTE, contrastRatio } from '@/lib/colors'
import { cn } from '@/lib/utils'

function isValidHex(v: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(v)
}

function hasAdequateContrast(hex: string): boolean {
  return contrastRatio(hex, '#ffffff') >= 3.0 || contrastRatio(hex, '#1a1a1a') >= 3.0
}

interface BucketColorPickerProps {
  color: string
  onChange: (color: string) => void
  disabled?: boolean
}

/** Popover body for picking a bucket color — reusable wherever anchored. */
export function ColorPickerFields({
  color,
  onChange,
  onClose,
}: {
  color: string
  onChange: (color: string) => void
  onClose?: () => void
}) {
  const [hexInput, setHexInput] = useState(color)

  function selectColor(hex: string) {
    onChange(hex)
    setHexInput(hex)
  }

  function handleHexBlur() {
    const val = hexInput.startsWith('#') ? hexInput : `#${hexInput}`
    if (isValidHex(val)) {
      onChange(val)
    } else {
      setHexInput(color)
    }
  }

  function handleHexKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.currentTarget.blur()
    }
    if (e.key === 'Escape') {
      setHexInput(color)
      onClose?.()
    }
  }

  const warn = !hasAdequateContrast(color)

  return (
    <>
      {/* Palette grid */}
        <div className="grid grid-cols-4 gap-1.5 mb-3">
          {BUCKET_PALETTE.map((hex) => (
            <button
              key={hex}
              type="button"
              onClick={() => selectColor(hex)}
              className={cn(
                'w-8 h-8 rounded-md transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1',
                color === hex && 'ring-2 ring-offset-2 ring-foreground',
              )}
              style={{ backgroundColor: hex }}
              aria-label={hex}
              aria-pressed={color === hex}
            />
          ))}
        </div>

        {/* Hex input */}
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded flex-shrink-0 border border-border"
            style={{ backgroundColor: isValidHex(hexInput) ? hexInput : color }}
          />
          <input
            type="text"
            value={hexInput}
            onChange={(e) => setHexInput(e.target.value)}
            onBlur={handleHexBlur}
            onKeyDown={handleHexKeyDown}
            maxLength={7}
            placeholder="#000000"
            className="flex-1 text-xs font-mono bg-muted rounded px-2 py-1 border border-input focus:outline-none focus:ring-1 focus:ring-ring"
            aria-label="Hex color value"
          />
        </div>

      {/* Contrast warning */}
      {warn && (
        <p className="flex items-center gap-1 text-xs text-amber-600 mt-2">
          <AlertTriangle size={12} />
          Low contrast — text may be hard to read
        </p>
      )}
    </>
  )
}

export function BucketColorPicker({ color, onChange, disabled }: BucketColorPickerProps) {
  const [open, setOpen] = useState(false)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'w-5 h-5 rounded-full ring-2 ring-offset-2 ring-offset-background transition-opacity',
            disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer hover:opacity-80',
          )}
          style={{ backgroundColor: color, boxShadow: `0 0 0 2px var(--background), 0 0 0 4px ${color}` }}
          aria-label={`Bucket color: ${color}. Click to change.`}
        />
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3" align="start">
        <ColorPickerFields key={open ? 'open' : 'closed'} color={color} onChange={onChange} onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  )
}
